import { useEffect, useRef } from "react";

/**
 * Animated turquoise neural network background.
 * Pure SVG + canvas-free: nodes orbit and connect with lines drawn between near nodes.
 * Lightweight, no images.
 */
const NeuralNetworkBackground = ({ density = 32, className = "" }) => {
  const svgRef = useRef(null);
  const rafRef = useRef(null);
  const nodesRef = useRef([]);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const W = 1600;
    const H = 900;

    // Create nodes with random positions and velocities
    const nodes = Array.from({ length: density }, () => ({
      x: Math.random() * W,
      y: Math.random() * H,
      vx: (Math.random() - 0.5) * 0.25,
      vy: (Math.random() - 0.5) * 0.25,
      r: 1.2 + Math.random() * 2.2,
      pulse: Math.random() * Math.PI * 2,
    }));
    nodesRef.current = nodes;

    const lineGroup = svg.querySelector("#lines");
    const nodeGroup = svg.querySelector("#nodes");

    // Pre-create elements
    nodeGroup.innerHTML = "";
    nodes.forEach((n, i) => {
      const c = document.createElementNS("http://www.w3.org/2000/svg", "circle");
      c.setAttribute("data-i", i);
      c.setAttribute("r", n.r);
      c.setAttribute("fill", "#00C8D7");
      c.setAttribute("opacity", "0.8");
      nodeGroup.appendChild(c);
    });

    const tick = () => {
      // Update positions
      nodes.forEach((n) => {
        n.x += n.vx;
        n.y += n.vy;
        if (n.x < 0 || n.x > W) n.vx *= -1;
        if (n.y < 0 || n.y > H) n.vy *= -1;
        n.pulse += 0.03;
      });

      // Update node DOM (positions + pulsing opacity)
      const circles = nodeGroup.children;
      for (let i = 0; i < circles.length; i++) {
        const n = nodes[i];
        circles[i].setAttribute("cx", n.x.toFixed(1));
        circles[i].setAttribute("cy", n.y.toFixed(1));
        circles[i].setAttribute("opacity", (0.4 + 0.4 * Math.abs(Math.sin(n.pulse))).toFixed(2));
      }

      // Build lines between near nodes
      const maxDist = 230;
      const parts = [];
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < maxDist) {
            const op = (1 - d / maxDist) * 0.45;
            parts.push(
              `<line x1="${nodes[i].x.toFixed(1)}" y1="${nodes[i].y.toFixed(1)}" x2="${nodes[j].x.toFixed(1)}" y2="${nodes[j].y.toFixed(1)}" stroke="#00C8D7" stroke-width="0.6" opacity="${op.toFixed(3)}"/>`
            );
          }
        }
      }
      lineGroup.innerHTML = parts.join("");

      rafRef.current = requestAnimationFrame(tick);
    };
    tick();

    return () => cancelAnimationFrame(rafRef.current);
  }, [density]);

  return (
    <svg
      ref={svgRef}
      viewBox="0 0 1600 900"
      preserveAspectRatio="xMidYMid slice"
      className={`absolute inset-0 w-full h-full ${className}`}
      aria-hidden="true"
      data-testid="neural-network-bg"
    >
      <defs>
        <radialGradient id="vignette" cx="50%" cy="50%" r="60%">
          <stop offset="0%" stopColor="rgba(10,10,10,0)" />
          <stop offset="100%" stopColor="rgba(10,10,10,1)" />
        </radialGradient>
      </defs>
      <g id="lines" />
      <g id="nodes" />
      <rect width="1600" height="900" fill="url(#vignette)" />
    </svg>
  );
};

/**
 * Gold scale of justice as an oversized faint watermark behind hero text.
 */
export const ScaleWatermark = () => (
  <div className="scale-watermark" aria-hidden="true" data-testid="scale-watermark">
    <svg viewBox="0 0 512 512" className="w-[min(90vw,900px)] h-auto" fill="none">
      <g stroke="#B8860B" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        {/* Central pillar */}
        <line x1="256" y1="80" x2="256" y2="430" />
        {/* Base */}
        <path d="M170 440 H342" />
        <path d="M256 430 L210 470 H302 L256 430 Z" />
        {/* Top beam */}
        <line x1="120" y1="140" x2="392" y2="140" />
        {/* Left chain + pan */}
        <line x1="160" y1="140" x2="120" y2="210" />
        <line x1="160" y1="140" x2="200" y2="210" />
        <path d="M90 210 H230 L200 280 H120 Z" />
        {/* Right chain + pan */}
        <line x1="352" y1="140" x2="312" y2="210" />
        <line x1="352" y1="140" x2="392" y2="210" />
        <path d="M282 210 H422 L392 280 H312 Z" />
        {/* Top ornament */}
        <circle cx="256" cy="80" r="14" />
      </g>
    </svg>
  </div>
);

export default NeuralNetworkBackground;
